import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  Cloud,
  Database,
  FileStack,
  Filter,
  Layers,
  Shield,
  Upload,
  Users,
  GitBranch,
} from 'lucide-react';
import './LandingPage.css';
import { getToken, validateSession, clearSession } from './auth';

const FEATURES = [
  {
    icon: Users,
    title: 'Client records',
    description: 'Manage client profiles, plot details, project heads, and assigned architects in one place.'
  },
  {
    icon: GitBranch,
    title: 'Status workflow',
    description: 'Track every case from Inward → In Progress → Outward → Completed with color-coded badges.'
  },
  {
    icon: FileStack,
    title: 'Multiple attachments',
    description: 'Upload drawings, documents, and external links - multiple files per client.'
  },
  {
    icon: Filter,
    title: 'Search & filters',
    description: 'Find clients quickly by area, status, project head, or free-text search with sorting.'
  },
  {
    icon: Upload,
    title: 'Bulk import',
    description: 'Import clients from Excel or CSV templates for faster onboarding.'
  },
  {
    icon: Shield,
    title: 'Role-based access',
    description: 'Admin and architect roles with secure JWT login and permission controls.'
  }
];

const WORKFLOW_STEPS = [
  { key: 'inward', label: 'Inward', desc: 'Client received' },
  { key: 'in_progress', label: 'In Progress', desc: 'Work ongoing' },
  { key: 'outward', label: 'Outward', desc: 'Sent out' },
  { key: 'completed', label: 'Completed', desc: 'Case closed' }
];

const HERO_STATS = [
  { icon: Building2, label: 'Multi-area support' },
  { icon: Cloud, label: 'Scalable file storage' },
  { icon: Layers, label: 'Status pipeline' }
];

export default function LandingPage() {
  const [navScrolled, setNavScrolled] = useState(false);
  const [sessionState, setSessionState] = useState(() => (getToken() ? 'checking' : 'guest'));

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!getToken()) {
        if (!cancelled) setSessionState('guest');
        return;
      }
      const user = await validateSession();
      if (cancelled) return;
      if (user) setSessionState('authed');
      else {
        clearSession();
        setSessionState('guest');
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  if (sessionState === 'checking') {
    return null;
  }

  if (sessionState === 'authed') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="landing">
      <header className={`topbar landing-nav${navScrolled ? ' landing-nav--scrolled' : ''}`}>
        <Link to="/" className="topbar-brand">
          <Database size={24} />
          <span>Client Management</span>
        </Link>
        <nav className="landing-nav-links">
          <a href="#features">Features</a>
          <a href="#workflow">Workflow</a>
        </nav>
        <Link to="/login" className="btn landing-cta-nav">
          Sign in
          <ArrowRight size={16} />
        </Link>
      </header>

      <section className="landing-hero">
        <div className="landing-hero-inner">
          <div className="landing-hero-copy">
            <span className="landing-eyebrow">Architecture &amp; client CRM</span>
            <h1>
              Manage clients, projects, and documents -{' '}
              <span className="landing-highlight">without the chaos</span>
            </h1>
            <p className="landing-hero-text">
              A modern workspace for firms handling MIDC, PMRDA, PCMC, and more.
              Track status, store attachments, and keep your team aligned.
            </p>
            <div className="landing-hero-actions">
              <Link to="/login" className="btn btn-primary landing-hero-btn">
                Get started
                <ArrowRight size={18} />
              </Link>
              <a href="#features" className="landing-hero-link">
                Explore features
                <ArrowRight size={16} />
              </a>
            </div>
            <div className="landing-hero-stats-bar">
              {HERO_STATS.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <React.Fragment key={stat.label}>
                    {index > 0 && <div className="landing-stat-divider" aria-hidden="true" />}
                    <div className="landing-stat-item">
                      <Icon size={18} />
                      <span>{stat.label}</span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div className="landing-hero-visual" aria-hidden="true">
            <div className="landing-mock-glow" />
            <div className="landing-mock-card landing-mock-card--primary">
              <div className="landing-mock-header">
                <span className="landing-mock-dot landing-mock-dot--close" />
                <span className="landing-mock-dot landing-mock-dot--min" />
                <span className="landing-mock-dot landing-mock-dot--max" />
              </div>
              <div className="landing-mock-body">
                <div className="landing-mock-row">
                  <span className="landing-mock-label">Client</span>
                  <span className="landing-mock-value">Vision 101</span>
                </div>
                <div className="landing-mock-row">
                  <span className="landing-mock-label">Area</span>
                  <span className="landing-mock-value">MIDC</span>
                </div>
                <div className="landing-mock-row">
                  <span className="landing-mock-label">Status</span>
                  <span className="status-badge status-in_progress">In Progress</span>
                </div>
                <div className="landing-mock-row">
                  <span className="landing-mock-label">Files</span>
                  <span className="landing-mock-value">4 attachments</span>
                </div>
              </div>
            </div>
            <div className="landing-mock-card landing-mock-card--secondary">
              <div className="landing-mock-mini-stat">
                <strong>128</strong>
                <span>Active clients</span>
              </div>
              <div className="landing-mock-mini-stat">
                <strong>24</strong>
                <span>In progress</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="landing-section">
        <div className="landing-section-inner">
          <div className="landing-section-head">
            <h2>Everything your firm needs</h2>
            <p>Built for architects and admins who manage high-volume client work daily.</p>
          </div>
          <div className="landing-features">
            {FEATURES.map((feature) => {
              const Icon = feature.icon;
              return (
                <article key={feature.title} className="landing-feature-card">
                  <div className="landing-feature-icon">
                    <Icon size={22} />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section id="workflow" className="landing-section landing-section--alt">
        <div className="landing-section-inner">
          <div className="landing-section-head">
            <h2>Clear client status workflow</h2>
            <p>Know exactly where each file stands - from inward to completed.</p>
          </div>
          <div className="landing-timeline">
            <div className="landing-timeline-track" aria-hidden="true" />
            {WORKFLOW_STEPS.map((step, index) => (
              <div
                key={step.key}
                className={`landing-timeline-step landing-timeline-step--${step.key}`}
              >
                <div className="landing-timeline-marker">
                  <span className="landing-timeline-number">{index + 1}</span>
                </div>
                <div className="landing-timeline-content">
                  <span className={`status-badge status-${step.key}`}>{step.label}</span>
                  <p>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-cta">
        <div className="landing-cta-inner">
          <h2>Ready to streamline your client operations?</h2>
          <p>Sign in to access your dashboard, manage clients, and upload documents.</p>
          <Link to="/login" className="btn btn-primary landing-cta-btn">
            Sign in to dashboard
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-brand landing-brand--footer">
            <Database size={20} />
            <span>Client Management System</span>
          </div>
          <p>© {new Date().getFullYear()} Client Management. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
